// @flow
import {
  getPublicResponsePacket,
  getResponsePacket,
  resendMessageCodeLink,
} from 'api'
import Loading from 'components/Loading'
import ModalFormSecurity from 'components/Modal/ModalFormSecurity'
import ModalFormUnavailable from 'components/Modal/ModalFormUnavailable'
import ResourceFill from 'components/ResourceFill'
import useQuery from 'hooks/useQuery'
import {
  getPublicResponse,
  getResponse,
} from 'queries/responses/services/loadResponses'
import * as React from 'react'
import { useHistory, useParams } from 'react-router-dom'
import type { PacketResponse } from 'types/packet-response'
import type { Response } from 'types/response'
import { ZentakeContext } from 'utils/ZentakeContext'

const sessionId = 'fillResponseCustomer'

function FillResponseCustomer(): React.Node {
  const { teamConfig, setTeamConfig } = React.useContext(ZentakeContext)

  const history = useHistory()
  const { messageCode } = useParams()
  const query = useQuery()

  const formOfficeUse = history.location.pathname.includes(
    'fillFormOfficeUseQuestions'
  )

  const packetOfficeUse = history.location.pathname.includes(
    'fillPacketOfficeUseQuestions'
  )

  const officeMode = formOfficeUse || packetOfficeUse

  const responseType = query.get('response_type')
  const isKiosk = query.get('isKiosk') === 'true'

  const [ready, setReady] = React.useState(false)
  const [isLinkExpired, setIsLinkExpired] = React.useState(false)
  const [sessions, setSessions] = React.useState({
    [sessionId]: {
      id: 'session',
      messageCode,
      packetResponse: null,
      responses: [],
      isSubmitted: false,
      customer: null,
    },
  })
  const [_packetResponse, setPacketResponse] = React.useState(null)

  const updateTeamConfig = React.useCallback(
    (packetResponse) => {
      if (teamConfig) return

      setTeamConfig(packetResponse?.team?.config)
    },
    [setTeamConfig, teamConfig]
  )

  React.useEffect(() => {
    if (!messageCode) return

    const fetchPacketResponse = async () => {
      let packetResponse: PacketResponse

      if (isKiosk || officeMode) {
        packetResponse = await getResponsePacket(messageCode, {
          with_response_details: true,
        })
      } else {
        packetResponse = await getPublicResponsePacket(messageCode, {
          with_response_details: false,
        })
      }

      setSessions((sessions) => ({
        [sessionId]: {
          ...sessions[sessionId],
          packetResponse,
          responses: packetResponse.responses,
          isSubmitted: packetResponse.is_submitted,
          customer: packetResponse.customer,
        },
      }))

      setPacketResponse(packetResponse)
    }

    const fetchResponse = async () => {
      let response: Response
      if (isKiosk || officeMode) {
        response = await getResponse(messageCode)
      } else {
        response = await getPublicResponse(messageCode)
      }

      setSessions((sessions) => ({
        [sessionId]: {
          ...sessions[sessionId],
          responses: [response],
          isSubmitted: response.is_submitted,
          customer: response.customer,
        },
      }))
    }

    const fetchResource = async () => {
      if (responseType === 'packet' || packetOfficeUse) {
        await fetchPacketResponse()
        return
      }

      await fetchResponse()
    }

    const resendLink = async () => {
      try {
        await resendMessageCodeLink(messageCode)
      } catch {
        // do nothing
      }
      setIsLinkExpired(true)
    }

    const doFetch = async () => {
      try {
        await fetchResource()
      } catch (error) {
        if (error?.detail?.detail === 'Response is already submitted.') {
          history.push(
            `/r/${messageCode}/submitted${history?.location?.search ?? ''}`
          )
        }
        if (
          [
            error?.detail?.detail,
            error?.message,
            error?.response?.data?.detail,
          ].includes('Message code is invalid or expired.')
        ) {
          await resendLink()
        }
      } finally {
        setReady(true)
      }
    }

    doFetch()
  }, [history, isKiosk, messageCode, officeMode, packetOfficeUse, responseType])

  React.useEffect(() => {
    if (!_packetResponse) return

    updateTeamConfig(_packetResponse)
  }, [_packetResponse, updateTeamConfig])

  if (!ready) return <Loading skipLogoutMessage />

  if (isLinkExpired) {
    return (
      <ModalFormSecurity
        hideButton
        isVisible
        description={
          <>
            Protecting your data is our top priority.{' '}
            <u>This link has expired.</u> To keep your information secure, we
            have sent you a new link.
          </>
        }
        title="For your security, We’ve sent you a new link"
      />
    )
  }

  const fillSession = sessions[sessionId]
  const { packetResponse, responses } = fillSession
  const hasNoResponse = ready && !responses.length && !packetResponse

  if (
    hasNoResponse ||
    (responses[0] && responses[0]?.form?.enabled === false) ||
    (packetResponse && packetResponse?.packet?.enabled === false)
  ) {
    return (
      <ModalFormUnavailable
        hideButton
        isVisible
        description={`Sorry, this ${
          responseType === 'packet' ? 'packet' : 'form'
        } is not available anymore`}
        title={`${responseType === 'packet' ? 'Packet' : 'Form'} not available`}
      />
    )
  }

  if (!sessions[sessionId].responses.length) return null

  return (
    <ResourceFill
      redirectOnSubmitted
      isPacket={responseType === 'packet' || packetOfficeUse}
      mode={officeMode ? 'office' : 'client'}
      sessionId={sessionId}
      sessions={sessions}
      setSessions={setSessions}
    />
  )
}

export default FillResponseCustomer
